import { FC } from 'react';
import strings from '../../../language/Swedish';
import styled from 'styled-components';
import tw from 'twin.macro';

import { Icon } from '../../../components';
import { Icons } from '../../../enums';
import {
  formatDateToString,
  formatPrice,
  getHomeTypeLanguageString,
  getOwnershipLanguageString,
} from '../../../helpers';

const StyledInformationBox = styled.div<{ $small?: boolean }>`
  ${tw` rounded-lg bg-white/80 border border-slate-200 p-4 space-y-6 text-xs`};
  ${({ $small }) => ($small ? tw`basis-1/2` : tw`max-w-sm mx-auto`)}
`;

const UnorderedList = styled.ul`
  ${tw`flex-1 space-y-3`};
`;

const StyledListItem = styled.li<{ $small?: boolean }>`
  ${tw`flex items-baseline`};
  ${({ $small }) => ($small ? tw`flex-wrap gap-x-1 mt-1.5!` : tw`gap-2`)}
`;

const StyledIcon = styled(Icon)`
  ${tw`translate-y-0.5 text-slate-500`};
`;

const Text = styled.span`
  ${tw`text-slate-500 shrink-0 w-28`};
`;

const Values = styled.div`
  ${tw`flex gap-2 flex-wrap`};
`;

interface Props {
  nrOfAds?: number;
  small?: boolean;
  filterData: {
    living_area_min?: number | null;
    living_area_max?: number | null;
    price_min?: number | null;
    price_max?: number | null;
    rooms_min?: number | null;
    rooms_max?: number | null;
    construction_year_min?: number | null;
    construction_year_max?: number | null;
    monthly_fee_min?: number | null;
    monthly_fee_max?: number | null;
    registered_date_min?: Date | null;
    registered_date_max?: Date | null;
    ownership_type?: string[] | null;
    home_type?: string[] | null;
  };
}

const InformationBox: FC<Props> = ({ nrOfAds, filterData, small }) => {
  //same filters everywhere
  const hasEnabledLivingArea =
    filterData.living_area_min !== null &&
    filterData.living_area_min !== undefined;
  const hasEnabledPrice =
    filterData.price_min !== null && filterData.price_min !== undefined;
  const hasEnabledRooms =
    filterData.rooms_min !== null && filterData.rooms_min !== undefined;
  const hasEnabledConstructionDate =
    filterData.construction_year_min !== null &&
    filterData.construction_year_min !== undefined;

  //optional filters
  const hasEnabledMonthlyFee =
    filterData.monthly_fee_min !== null &&
    filterData.monthly_fee_min !== undefined;
  const hasEnabledRegisteredDate =
    filterData.registered_date_min !== null &&
    filterData.registered_date_min !== undefined;

  const adDateString =
    filterData.registered_date_min !== null &&
    filterData.registered_date_max !== null &&
    filterData.registered_date_min !== undefined &&
    filterData.registered_date_max !== undefined
      ? `${formatDateToString(
          filterData.registered_date_min
        )} - ${formatDateToString(filterData.registered_date_max)}`
      : '';

  const homeType = filterData.home_type;
  const ownership = filterData.ownership_type;

  return (
    <StyledInformationBox $small={small}>
      <UnorderedList>
        {nrOfAds && (
          <StyledListItem $small={small}>
            <StyledIcon icon={Icons.speaker} />
            <Text>{`${strings.market.ads}:`}</Text>
            <span>{formatPrice(nrOfAds)}</span>
          </StyledListItem>
        )}
        {homeType && (
          <StyledListItem $small={small}>
            <StyledIcon icon={Icons.bed} />
            <Text>{`${strings.market.housingType}:`}</Text>
            <Values>
              {homeType &&
                homeType.map((item) => (
                  <span key={item}>{getHomeTypeLanguageString(item)}</span>
                ))}
            </Values>
          </StyledListItem>
        )}
        {ownership && (
          <StyledListItem $small={small}>
            <StyledIcon icon={Icons.key} />
            <Text>{`${strings.market.ownership}:`}</Text>
            <Values>
              {ownership &&
                ownership.map((item) => (
                  <span key={item}>{getOwnershipLanguageString(item)}</span>
                ))}
            </Values>
          </StyledListItem>
        )}
        {hasEnabledPrice && (
          <StyledListItem $small={small}>
            <StyledIcon icon={Icons.coins} />
            <Text>{`${strings.filter.price}:`}</Text>
            <span>{`${filterData.price_min}-${filterData.price_max} ${strings.filter.price_unit}`}</span>
          </StyledListItem>
        )}
        {hasEnabledLivingArea && (
          <StyledListItem $small={small}>
            <StyledIcon icon={Icons.area} />
            <Text>{`${strings.filter.living_area}:`}</Text>
            <span>{`${filterData.living_area_min}-${filterData.living_area_max} ${strings.filter.living_area_unit}`}</span>
          </StyledListItem>
        )}
        {hasEnabledMonthlyFee && (
          <StyledListItem $small={small}>
            <StyledIcon icon={Icons.dollar} />
            <Text>{`${strings.filter.monthly_fee}:`}</Text>
            <span>{`${filterData.monthly_fee_min}-${filterData.monthly_fee_max} ${strings.filter.monthly_fee_unit}`}</span>
          </StyledListItem>
        )}
        {hasEnabledConstructionDate && (
          <StyledListItem $small={small}>
            <StyledIcon icon={Icons.crane} />
            <Text>{`${strings.filter.construction_year}:`}</Text>
            <span>{`${filterData.construction_year_min}-${filterData.construction_year_max}`}</span>
          </StyledListItem>
        )}
        {hasEnabledRooms && (
          <StyledListItem $small={small}>
            <StyledIcon icon={Icons.bed} />
            <Text>{`${strings.filter.rooms}:`}</Text>
            <span>{`${filterData.rooms_min}-${filterData.rooms_max} ${strings.filter.rooms_unit}`}</span>
          </StyledListItem>
        )}
        {hasEnabledRegisteredDate && (
          <StyledListItem $small={small}>
            <StyledIcon icon={Icons.calendar} />
            <Text>{`${strings.market.adDate}:`}</Text>
            <span>{adDateString}</span>
          </StyledListItem>
        )}
      </UnorderedList>
    </StyledInformationBox>
  );
};

export default InformationBox;
